import React from 'react';
import PublicLayout from '../../components/layouts/PublicLayout';
import { useSEO, generateBreadcrumbSchema, generateWebPageSchema } from '../../hooks/useSEO';
import { usePublicTranslation } from '../../hooks/usePublicTranslation';

const BoardPage: React.FC = () => {
  const { t, currentLanguage } = usePublicTranslation();
  const board = t?.about?.board;

  if (!board) return null;

  // Dados da equipe de gestão usando traduções
  const managementTeam = [
    {
      name: board.team.member1.name,
      title: board.team.member1.title,
      description: board.team.member1.description,
      imageUrl: '/assets/imagens/1639419791973.jpeg',
      linkedinUrl: 'https://www.linkedin.com/in/jos%C3%A9-geraldo-rolim-zacarias-82785425',
      expertise: board.team.member1.expertise
    },
    {
      name: board.team.member2.name,
      title: board.team.member2.title,
      description: board.team.member2.description,
      imageUrl: '/assets/imagens/1581096148238.jpeg',
      linkedinUrl: 'https://www.linkedin.com/in/arthur-pinheiro-356ba218/',
      expertise: board.team.member2.expertise
    },
    {
      name: board.team.member3.name,
      title: board.team.member3.title,
      description: board.team.member3.description,
      imageUrl: '/assets/imagens/1749778676637.jpeg',
      linkedinUrl: 'https://www.linkedin.com/in/9165014b/',
      expertise: board.team.member3.expertise
    },
    {
      name: board.team.technicalTeam.name,
      title: board.team.technicalTeam.title,
      description: board.team.technicalTeam.description,
      imageUrl: 'https://placehold.co/200x200/7F8C8D/FFFFFF?text=CS',
      linkedinUrl: 'https://www.linkedin.com/company/108842349/',
      expertise: board.team.technicalTeam.expertise
    }
  ];

  useSEO({
    title: board.title,
    description: board.subtitle,
    path: '/about/board',
    language: currentLanguage as 'pt-br' | 'en' | 'es',
    structuredData: [
      generateBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Sobre', url: '/about' },
        { name: 'Board', url: '/about/board' }
      ]),
      generateWebPageSchema({
        name: board.title,
        description: board.subtitle,
        url: '/about/board',
        datePublished: "2025-01-20",
        dateModified: "2025-01-20"
      })
    ]
  });

  return (
    <PublicLayout>

      <main className="flex-grow">
        {/* Hero Section - Matching User Snippet (Dark to Primary Dark 135deg) */}
        <div className="bg-primary text-white py-12 md:py-20 bg-gradient-to-br from-dark to-primary-dark relative overflow-hidden">
          <div className="container mx-auto px-4 text-center relative z-10">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold mb-4 md:mb-6 animate-fade-in-up text-transparent bg-clip-text bg-gradient-to-br from-white to-secondary">
              {board?.title || "Board"}
            </h1>
            <p className="text-base sm:text-lg md:text-xl max-w-3xl mx-auto text-gray-200 animate-slide-up-fade">
              {board?.subtitle}
            </p>
          </div>
        </div>

        {/* Team Section */}
        <section className="py-12 md:py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-7xl mx-auto">
              {managementTeam.map((member, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-5 sm:p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 flex flex-col items-center text-center group h-full"
                >
                  <div className="w-28 h-28 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-full overflow-hidden mb-4 md:mb-6 border-4 border-white shadow-md group-hover:scale-105 transition-transform duration-300 relative">
                    <img
                      src={member.imageUrl}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <h3 className="text-xl md:text-2xl font-serif font-bold text-dark mb-2">
                    {member.name}
                  </h3>

                  <h4 className="text-xs md:text-sm font-bold text-primary uppercase tracking-wider mb-4 md:mb-6">
                    {member.title}
                  </h4>

                  <p className="text-gray-600 mb-6 md:mb-8 leading-relaxed text-sm">
                    {member.description}
                  </p>

                  <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2 mb-6 md:mb-8 mt-auto">
                    {member.expertise.map((skill: string, skillIndex: number) => (
                      <span
                        key={skillIndex}
                        className="bg-primary/10 text-primary-dark text-xs font-bold px-3 sm:px-4 py-1 sm:py-1.5 rounded-full border border-primary/20 hover:bg-primary/20 transition-colors"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>

                  <a
                    href={member.linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-primary font-bold text-sm hover:text-secondary transition-colors group-link"
                  >
                    {board.linkedinLabel}
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        <div className="h-8 md:h-16 bg-white"></div>
      </main>
    </PublicLayout>
  );
};

export default BoardPage;